<template>
	<div ref="dataLoaderElement" class="data-loader stretch-layout">

		<slot name="alwaysshow"/>
		<slot v-if="loadedData.length && (!isLoading || !replaceData)" v-bind:data="loadedData"/>

		<div v-if="!error && !loadedData.length && !isLoading" class="stretch-layout">
			<slot v-if="!filterExists || !$slots.missingsearch" name="missing"/>
			<slot v-else name="missingsearch"/>
		</div>

		<fetch-loader v-if="error || isLoading" :error="error" :pending="isLoading" :flat="props.flat" @retry="reload" fill class="w-full">
			<template v-if="$slots.loading" #loading>
				<slot name="loading"/>
			</template>
		</fetch-loader>

		<div v-else-if="((props.showButton && !props.onScroll) || $slots.button) && hasMore" class="flex justify-center py-20">
			<slot name="button">
				<btn ref="button" @click.prevent="loadNext()" :loading="isLoading">
					<Icon :icon="icons.more"/>
					<span>{{ $t('action.show_more') }}</span>
				</btn>
			</slot>
		</div>

		<span v-if="!isLoading && props.onScroll" class="block w-full h-1" v-intersect="{handler: onIntersect, options: {rootMargin: `${scrollLoadMargin}px`}}"/>

		<slot name="alwaysshow_below"/>

	</div>
</template>

<script setup lang="ts">
import {moreIcon} from "../../config";
import {onMounted, watch, ref} from 'vue';
import {useNuxtApp} from '#app';
import {useDataLoaderComponent, DataLoaderComponentEmits, DataLoaderComponentPropDefaults, type DataLoaderComponentProps} from "../../composables/useDataLoaderComponent";
import type {Ref} from "vue";

const {isHydrating, $auth} = useNuxtApp();

const props = withDefaults(defineProps<{

	// States if a button shall be shown below the inserted slot elements, which can load the next resources.
	showButton?: boolean,

	// The amount of pixels before the end of the content list to trigger a loading of the next entries when 'onScroll' is set
	scrollLoadMargin?: number,

	// Whether a flat loader shall be displayed
	flat?: boolean,

} & DataLoaderComponentProps>(), {
	...DataLoaderComponentPropDefaults,
	scrollLoadMargin: 250,
	simplePaginated: true,
	showButton: true,
});

const emit = defineEmits(DataLoaderComponentEmits);

// The HTML element on which the component is mounted
const dataLoaderElement: Ref<HTMLElement|null> = ref(null);

const {
	isLoading, loadedData, loadNext, load, reload, hasMore,
	filterExists,
	onIntersect,
	replaceData, error
} = useDataLoaderComponent(dataLoaderElement, props, emit);

defineExpose({
	loadNext,
	reload
})

const icons = {
	more: moreIcon,
};

// It's important to load the data on SSR and CSR the same way to prevent hydration mismatches.
// Additionally, loading data within the watcher on CSR when the auth state is already initialized might cause
// a "Cannot read properties of null (reading 'parentNode')" issue, whereby we load it via onMounted.
if (props.auth && (!$auth.initialized || isHydrating)) {
	watch(() => $auth.initialized, (newValue) => {
		if (newValue) {
			load();
		}
	}, {immediate: true});
} else if (props.lazy || (process.client && !isHydrating)) {
	onMounted(() => {
		load();
	});
} else {
	await load();
}

</script>